/**
 * Ranking for the country search on the request-access form.
 *
 * Kept free of imports so it can be unit-tested without a browser
 * (tests/country-search.spec.ts).
 */

export interface CountryOption {
  code: string;
  name: string;
}

/** The markets the portal is set up for; they win ties in the search. */
const MAIN_MARKETS = ["AU", "GB", "US", "NZ", "CA", "IE"];

/** What people type that is not the official country name. */
const COUNTRY_ALIASES: Record<string, string[]> = {
  US: ["usa", "america", "united states"],
  GB: ["uk", "britain", "great britain", "england"],
  AE: ["uae"],
};

/**
 * Score a country against the search text. 0 means "does not match".
 *
 * A fuzzy match listed "United States Minor Outlying Islands" above "United
 * States of America" for "united s", and "Ukraine" above "United Kingdom" for
 * "uk". A wrong pick sets the whole practice up for the wrong country, so the
 * order is explicit: the country code or a common short name typed in full,
 * then names that start with the search, then names with a word that starts
 * with it, then names that contain it. Within a group the portal's main
 * markets come first, then shorter names.
 */
export function countryScore(country: CountryOption, search: string): number {
  const query = search.trim().toLowerCase();
  if (!query) return 1;
  const name = country.name.toLowerCase();
  const code = country.code.toLowerCase();
  const aliases = COUNTRY_ALIASES[country.code] ?? [];
  const tieBreak =
    (MAIN_MARKETS.includes(country.code) ? 0.05 : 0) + 1 / (1000 + name.length);

  if (name === query || code === query || aliases.includes(query)) return 1;
  if (name.startsWith(query)) return 0.8 + tieBreak;
  if (aliases.some((alias) => alias.startsWith(query))) return 0.7 + tieBreak;
  if (name.split(/[\s,()-]+/).some((word) => word.startsWith(query))) {
    return 0.6 + tieBreak;
  }
  if (name.includes(query)) return 0.4 + tieBreak;
  return 0;
}

/**
 * The countries that match `search`, best match first. With an empty search
 * the list comes back unchanged (the API's alphabetical order).
 */
export function rankCountries<T extends CountryOption>(
  countries: T[],
  search: string,
): T[] {
  if (!search.trim()) return countries;
  return countries
    .map((country, index) => ({
      country,
      index,
      score: countryScore(country, search),
    }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((entry) => entry.country);
}
